import type {
  PublicAppointmentSession,
  PublicAvailabilitySlot,
  PublicManageAvailabilityResponse,
} from "@emr/contracts";
import { useCallback, useMemo } from "react";
import { addDays, firstOfMonth, laterDate, monthOf } from "../booking/availabilityCalendar";
import { DateTimePicker } from "../booking/DateTimePicker";
import { useAvailabilityCalendar } from "../booking/useAvailabilityCalendar";
import { Icon } from "../Icon";
import { localDateOf } from "../lib/appointmentTime";

/**
 * One search for new times: a span of dates (at most 63 days, as the contract allows) and the
 * physician handle, only when the patient chose one.
 */
export interface ManageTimesSearch {
  readonly fromDate: string;
  readonly toDate: string;
  readonly providerId?: string;
}

/** What the patient has picked so far for a new time, kept while they look and confirm. */
export interface TimesDraft {
  /** The chosen day; "" until the calendar picks one (the visit's own day when it has times). */
  readonly date: string;
  /** The calendar month on screen. */
  readonly month: string;
  /** The physician handle to search, or "" for any available physician. */
  readonly providerId: string;
  readonly slot: PublicAvailabilitySlot | null;
  /** Who the chosen time is with, as the times named them; null for a clinic-assigned time. */
  readonly physician: string | null;
}

/** The visit's own date on the clinic's clock, or today when it has passed. */
function visitDay(session: PublicAppointmentSession): string {
  const today = localDateOf(new Date(), session.timeZone);
  const visitDate = localDateOf(session.startAt, session.timeZone);
  return visitDate < today ? today : visitDate;
}

/** Starts on the visit's own month (on the clinic's clock) with the patient's own physician. */
export function initialTimesDraft(session: PublicAppointmentSession): TimesDraft {
  return {
    date: "",
    month: monthOf(visitDay(session)),
    providerId: session.providerId ?? "",
    slot: null,
    physician: null,
  };
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(-2)
    .map((part) => part[0])
    .join("");
}

/**
 * The booking flow's date and time picker for a new time (PA-9): the same visit type, in the
 * clinic's local time. Where the patient chose the physician they may keep theirs or take any
 * available one; where the clinic assigns one there is no choice and no time names one. The
 * manage link never asks for an existing patient's pass: the link and date of birth are enough.
 * The calendar asks for a month's times in one search (counted from the visit's month), and the
 * clinic's own booking rules decide how far ahead times exist.
 */
export function RescheduleTimes({
  session,
  draft,
  update,
  loadTimes,
  reload,
}: {
  readonly session: PublicAppointmentSession;
  readonly draft: TimesDraft;
  readonly update: (next: Partial<TimesDraft>) => void;
  readonly loadTimes: (
    search: ManageTimesSearch,
    signal: AbortSignal,
  ) => Promise<PublicManageAvailabilityResponse>;
  /** Bumped to read the times again, as after a time was just taken. */
  readonly reload: number;
}) {
  const today = localDateOf(new Date(), session.timeZone);
  const preferred = visitDay(session);
  // The server applies the clinic's booking window; a year past the visit bounds the paging.
  const lastDay = addDays(preferred, 365);
  const anchor = laterDate(firstOfMonth(monthOf(preferred)), today);
  // The service leaves the booking's own time out; never offer it as a new one.
  const keep = useCallback(
    (slot: PublicAvailabilitySlot) =>
      !(slot.startAt === session.startAt && slot.providerId === session.providerId),
    [session.startAt, session.providerId],
  );
  const availability = useAvailabilityCalendar({
    queryKey: ["manage-availability", session.version, draft.providerId, reload],
    load: (range, signal) =>
      loadTimes(
        { ...range, ...(draft.providerId === "" ? {} : { providerId: draft.providerId }) },
        signal,
      ),
    month: draft.month,
    selected: draft.date,
    anchor,
    firstDay: today,
    lastDay,
    enabled: true,
    retry: false,
    keep,
  });
  const providers = availability.providers;
  const physicianName = useMemo(() => {
    const names = new Map((providers ?? []).map((provider) => [provider.id, provider.displayName]));
    return (providerId: string) =>
      names.get(providerId) ??
      (providerId === session.providerId ? session.providerDisplayName : "Clinic clinician");
  }, [providers, session.providerId, session.providerDisplayName]);
  const choosesPhysician = session.providerId !== undefined;
  const onDate = useCallback(
    (date: string) => update({ date, slot: null, physician: null }),
    [update],
  );
  const onMonthChange = useCallback((month: string) => update({ month }), [update]);
  const fresh = { date: "", month: monthOf(preferred), slot: null, physician: null } as const;

  return (
    <>
      {choosesPhysician ? (
        <fieldset className="booking-fieldset">
          <legend>Clinician</legend>
          <div className="booking-provider-grid">
            <label className={draft.providerId === session.providerId ? "selected" : ""}>
              <input
                type="radio"
                name="reschedule-provider"
                checked={draft.providerId === session.providerId}
                onChange={() => update({ providerId: session.providerId ?? "", ...fresh })}
              />
              <span className="booking-avatar">{initials(session.providerDisplayName)}</span>
              <span>
                <strong>{session.providerDisplayName}</strong>
                <small>Your current clinician</small>
              </span>
              <i>
                <Icon name="check" />
              </i>
            </label>
            <label className={draft.providerId === "" ? "selected" : ""}>
              <input
                type="radio"
                name="reschedule-provider"
                checked={draft.providerId === ""}
                onChange={() => update({ providerId: "", ...fresh })}
              />
              <span className="booking-avatar">Any</span>
              <span>
                <strong>Any available clinician</strong>
                <small>Most times</small>
              </span>
              <i>
                <Icon name="check" />
              </i>
            </label>
          </div>
        </fieldset>
      ) : (
        <p className="booking-time-note booking-assigned-note">
          <Icon name="person" />
          The clinic assigns the clinician for this visit.
        </p>
      )}
      <DateTimePicker
        headingLevel={3}
        availability={availability}
        month={draft.month}
        onMonthChange={onMonthChange}
        today={today}
        firstDay={today}
        lastDay={lastDay}
        date={draft.date}
        onDate={onDate}
        preferredDate={preferred}
        slot={draft.slot}
        onSlot={(slot) =>
          update({
            slot,
            physician: slot.providerId === undefined ? null : physicianName(slot.providerId),
          })
        }
        timeZone={session.timeZone ?? "UTC"}
        physicianName={physicianName}
        emptyHint={choosesPhysician ? "Pick another day or clinician." : "Pick another day."}
        name="appointment-slot"
      />
    </>
  );
}
